/**
 * Bounds for `<input type="date">` fields that write to the directory.
 *
 * A native date input accepts a one- or two-digit year: type "25" into the year
 * segment, tab out, and the field's value is "0025-11-20". The API stored that
 * verbatim, and twelve assignment rows were created that way by seven different
 * editors before these bounds existed.
 *
 * Two things are needed on every date field that saves, because they catch the
 * mistake at different moments:
 *
 *   1. Spread `dateInputBounds` onto the input. The browser then refuses to
 *      submit the surrounding <form> and shows its own message next to the
 *      field, which is the fastest feedback available.
 *   2. Call `validateDateFields` in the save handler. Inputs that save without
 *      a native form submit — a table cell that PATCHes on change, a modal
 *      whose button is not type="submit" — never reach step 1.
 *
 * The API enforces the same range (see backend/app/utils/date_validation.py);
 * this layer exists so the person typing finds out before the round trip.
 */

/** No directory record plausibly predates this, and every truncated year is below it. */
export const MIN_ENTRY_YEAR = 1900;

/** Far enough out for board terms and planned end dates; still catches a stray 9999. */
export const MAX_ENTRY_YEAR_OFFSET = 50;

/** Highest year accepted. Read from the clock so it does not go stale. */
export function maxEntryYear(): number {
  return new Date().getFullYear() + MAX_ENTRY_YEAR_OFFSET;
}

/** `min`/`max` attributes for a date input. Spread onto the element. */
export const dateInputBounds = {
  min: `${MIN_ENTRY_YEAR}-01-01`,
  max: `${maxEntryYear()}-12-31`,
} as const;

/** Strip a time component so API values ("2025-11-20T00:00:00") compare cleanly. */
function datePart(value: string): string {
  return value.split('T')[0];
}

/**
 * Validate one date field.
 *
 * @param value  The raw input value. Empty means "no date", which is valid.
 * @param label  Field name as the user sees it, e.g. "Start Date".
 * @returns An error message, or null when the value is fine.
 */
export function validateDateField(
  value: string | null | undefined,
  label: string
): string | null {
  if (!value) return null;

  const isoDate = datePart(value);
  const match = /^(\d{1,6})-(\d{2})-(\d{2})$/.exec(isoDate);
  if (!match) {
    return `${label} is not a valid date. Use the date picker or type MM/DD/YYYY.`;
  }

  const year = Number(match[1]);
  if (year < MIN_ENTRY_YEAR || year > maxEntryYear()) {
    return (
      `${label} has the year ${match[1]}, which is outside ${MIN_ENTRY_YEAR}-${maxEntryYear()}. ` +
      `Enter all four digits of the year — typing "25" gives you the year 0025, not 2025.`
    );
  }

  return null;
}

/**
 * Validate several date fields at once and return the first problem found.
 *
 * @example
 *   const dateError = validateDateFields([
 *     { value: formData.beginDate, label: 'Start Date' },
 *     { value: formData.endDate, label: 'End Date' },
 *   ]);
 *   if (dateError) { setError(dateError); return; }
 */
export function validateDateFields(
  fields: Array<{ value: string | null | undefined; label: string }>
): string | null {
  for (const field of fields) {
    const message = validateDateField(field.value, field.label);
    if (message) return message;
  }
  return null;
}
